import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';
import { listOnboardingTasks, updateOnboardingTask } from '@src/api/onboardingWorkflow';
import { fetchClient } from '@src/api/actions/clients';
import paths from '@src/paths';
import AdAccountCardStaffCard from '@src/components/cardVault/AdAccountCardStaffCard';
import CampaignLaunchDialog from '@src/components/onboarding/CampaignLaunchDialog';
import OnboardingEmailHistoryCard from '@src/components/onboarding/OnboardingEmailHistoryCard';
import LaunchStatusCard from '@src/components/onboarding/LaunchStatusCard';
import { CreativeFeedbackDialog, CreativeReplaceDialog } from '@src/components/creative/CreativeAssetDialogs';

export default function OnboardingChecklistPage() {
  const { clientSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const verticalParam = searchParams.get('vertical');
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notesDialog, setNotesDialog] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingTaskIds, setPendingTaskIds] = useState(new Set());

  // New-vertical flow
  const [nvDialogOpen, setNvDialogOpen] = useState(false);
  const [nvVerticals, setNvVerticals] = useState([]);
  const [nvVerticalId, setNvVerticalId] = useState('');
  const [nvStarting, setNvStarting] = useState(false);

  // Onboarding owner
  const [owner, setOwner] = useState(null);
  const [staffOptions, setStaffOptions] = useState([]);

  // Which client lane each vertical runs in
  const [verticalLanes, setVerticalLanes] = useState([]);

  // Signature gates the build; a vertical flow is launched at its first lead
  const [contract, setContract] = useState(null);
  const [verticalFirstLeadAt, setVerticalFirstLeadAt] = useState(null);

  // Archived (pre-v2) checklist history — lazy-loaded on expand
  const [archivedTasks, setArchivedTasks] = useState(null);
  const [archivedLoading, setArchivedLoading] = useState(false);

  const loadArchived = async () => {
    if (archivedTasks !== null) return;
    setArchivedLoading(true);
    try {
      const res = await listOnboardingTasks({ client_slug: clientSlug, archived: 1 });
      setArchivedTasks(res?.data?.results || []);
    } catch {
      setArchivedTasks([]);
    } finally {
      setArchivedLoading(false);
    }
  };

  // Creative reviews
  const [creativeReviews, setCreativeReviews] = useState([]);
  const [creativeUploadOpen, setCreativeUploadOpen] = useState(false);
  const [creativeAds, setCreativeAds] = useState([]);
  const [creativeVerticalId, setCreativeVerticalId] = useState('');
  const [creativeUploading, setCreativeUploading] = useState(false);

  // Client onboarding data (offers, assets)
  const [onboardingData, setOnboardingData] = useState(null);

  // Landing pages
  const [landingPages, setLandingPages] = useState([]);
  const [lpVerticals, setLpVerticals] = useState([]);
  const [lpDialogOpen, setLpDialogOpen] = useState(false);
  const [lpUrl, setLpUrl] = useState('');
  const [lpVerticalId, setLpVerticalId] = useState('');
  const [lpName, setLpName] = useState('');
  const [lpSaving, setLpSaving] = useState(false);

  // Selfbook calendar
  const [selfbookCalendar, setSelfbookCalendar] = useState(null);

  // Internal notes (for creative designer)
  const [internalNotes, setInternalNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const handleSaveInternalNotes = async () => {
    setNotesSaving(true);
    try {
      await defaultClient.request({
        url: `clients/${clientSlug}/onboarding/`,
        method: 'PATCH',
        payload: { internal_notes: internalNotes },
      });
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  };

  // Launch panel (Facebook campaign build)
  const [fbDialogOpen, setFbDialogOpen] = useState(false);

  const [replaceAsset, setReplaceAsset] = useState(null);
  const [feedbackAsset, setFeedbackAsset] = useState(null);
  const [sendingCreative, setSendingCreative] = useState(false);
  // Uploads and replacements wait here until "Send to client" sends them all at once.
  const unsentAds = creativeReviews.reduce((n, b) => n + (b.assets || []).filter(
    (a) => b.status === 'draft' || a.awaiting_send,
  ).length, 0);
  const sendCreative = async () => {
    setSendingCreative(true);
    try {
      const res = await defaultClient.request({ url: `app1/creative-review/${clientSlug}/send/`, method: 'POST' });
      toast.success(res?.data?.detail || 'Sent');
      refreshCreative(); loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send');
    } finally {
      setSendingCreative(false);
    }
  };

  const refreshCreative = () => {
    defaultClient.request({ url: `app1/creative-review/${clientSlug}/` })
      .then((res) => setCreativeReviews(res?.data?.results || []))
      .catch(() => {});
  };

  const refreshLandingPages = () => {
    if (!clientSlug) return;
    defaultClient.request({ url: `app1/landing-pages/${clientSlug}/` })
      .then((res) => {
        setLandingPages(res?.data?.results || []);
        setLpVerticals(res?.data?.verticals || []);
      })
      .catch(() => {});
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // All four fetches key off the slug — fire them in parallel.
      const [clientRes, tlRes, onbRes, taskRes] = await Promise.all([
        fetchClient(clientSlug),
        defaultClient.request({ url: `clients/${clientSlug}/onboarding/timeline/` }).catch(() => null),
        defaultClient.request({ url: `clients/${clientSlug}/onboarding/` }).catch(() => null),
        listOnboardingTasks({ client_slug: clientSlug, vertical: verticalParam || undefined }).catch(() => null),
      ]);
      setClient(clientRes?.data || null);
      if (tlRes?.data) {
        setTimeline(tlRes.data);
        setSelfbookCalendar(tlRes.data.selfbook_calendar || null);
      }
      if (onbRes?.data) {
        setOnboardingData(onbRes.data);
        setInternalNotes(onbRes.data.internal_notes || '');
      }
      setTasks(taskRes?.data?.results || []);
      setOwner(taskRes?.data?.owner || null);
      setVerticalLanes(taskRes?.data?.vertical_lanes || []);
      setContract(taskRes?.data?.contract || null);
      setVerticalFirstLeadAt(taskRes?.data?.vertical_first_lead_at || null);
    } catch {
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  }, [clientSlug, verticalParam]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    defaultClient.request({ url: 'tasks/onboarding-tasks/staff-options/' })
      .then((res) => setStaffOptions(res?.data || []))
      .catch(() => {});
  }, []);

  const openNvDialog = useCallback(async () => {
    setNvVerticalId('');
    setNvDialogOpen(true);
    try {
      const res = await defaultClient.request({ url: 'dashboard/verticals/all-options/' });
      setNvVerticals(res?.data || []);
    } catch { setNvVerticals([]); }
  }, []);

  // Deep link from the Client Hub: ?start_vertical=1 lands with the vertical
  // picker already open.
  useEffect(() => {
    if (searchParams.get('start_vertical')) {
      openNvDialog();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('start_vertical');
        return next;
      }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetOwner = async (userId) => {
    try {
      const res = await defaultClient.request({
        url: 'tasks/onboarding-tasks/set-owner/',
        method: 'POST',
        payload: { client_slug: clientSlug, user_id: userId || null },
      });
      setOwner(res?.data?.owner || null);
      toast.success(userId ? 'Owner assigned' : 'Owner cleared');
    } catch {
      toast.error('Failed to set owner');
    }
  };

  useEffect(() => { if (clientSlug) { refreshCreative(); refreshLandingPages(); } }, [clientSlug]);

  const handleSetStatus = async (task, newStatus) => {
    // Optimistic: flip locally right away, revert on failure. Only the
    // toggled row locks while its PATCH is in flight — the rest of the
    // checklist stays clickable.
    const prevStatus = task.status;
    setPendingTaskIds((prev) => new Set(prev).add(task.id));
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t)));
    try {
      await updateOnboardingTask(task.id, { status: newStatus });
    } catch {
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: prevStatus } : t)));
      toast.error('Failed to update task');
    } finally {
      setPendingTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  };

  const handleToggleComplete = (task) =>
    handleSetStatus(task, task.status === 'complete' ? 'not_started' : 'complete');

  const handleSaveNotes = async () => {
    if (!notesDialog) return;
    setSaving(true);
    try {
      await updateOnboardingTask(notesDialog, { notes: notesValue });
      setTasks((prev) => prev.map((t) => t.id === notesDialog ? { ...t, notes: notesValue } : t));
      setNotesDialog(null);
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  // Group tasks by phase. N/A tasks stay visible but drop out of every
  // progress denominator.
  const phases = {};
  const parentTasks = tasks.filter((t) => !t.parent_template_key);
  parentTasks.forEach((t) => { const phase = t.phase || 'Other'; if (!phases[phase]) phases[phase] = []; phases[phase].push(t); });
  const countedTasks = parentTasks.filter((t) => t.status !== 'not_applicable');
  const totalTasks = countedTasks.length;
  const completedTasks = countedTasks.filter((t) => t.status === 'complete').length;
  const progress = totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const clientName = client?.dba_name || client?.name || clientSlug;

  // Whose move: the next open items on each side, in registry order.
  const bySort = (a, b) => (a.template_sort_order || 0) - (b.template_sort_order || 0);
  const openItems = countedTasks.filter((t) => t.status !== 'complete');
  const ourMoves = openItems.filter((t) => t.owner === 'guru').sort(bySort).slice(0, 3);
  const clientMoves = openItems.filter((t) => t.owner === 'client').sort(bySort).slice(0, 3);
  const idleDays = (t) => (t?.updated_at
    ? Math.floor((Date.now() - new Date(t.updated_at).getTime()) / 86400000)
    : null);
  const contractPending = !verticalParam && !!contract?.pending;
  const CONTRACT_TEXT = {
    none: 'No contract has been sent yet.',
    draft: 'The contract is drafted but not sent.',
    sent: 'The contract was sent but not opened.',
    viewed: 'The client opened the contract but has not signed.',
  };

  const verticalName = verticalParam ? (tasks[0]?.vertical_name || '') : '';
  const visibleCreative = verticalParam
    ? creativeReviews.filter((b) => (b.vertical_name || '') === verticalName)
    : creativeReviews;
  const visibleLps = verticalParam
    ? landingPages.filter((lp) => (lp.vertical_name || '') === verticalName)
    : landingPages;

  const EVIDENCE_LINKS = {
    webhook_configured: paths.webhookSetup(clientSlug),
    posting_integration_testing: paths.webhookSetup(clientSlug),
    lead_delivery_confirmed: paths.webhookSetup(clientSlug),
    landing_pages_tested: paths.webhookSetup(clientSlug),
  };
  const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

  // Timeline data
  const completionSteps = (timeline?.steps || []).filter((s) => s.key !== 'approvals');
  const clientActions = timeline?.client_actions || [];
  const estimatedLaunch = timeline?.estimated_launch;
  const launchReason = timeline?.estimated_launch_reason || '';

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h5" fontWeight={700}>Onboarding: {clientName}</Typography>
            {verticalParam && (
              <Chip
                label={`New Vertical: ${tasks[0]?.vertical_name || ''}`}
                size="small" color="secondary" sx={{ fontWeight: 700 }}
                onDelete={() => setSearchParams({})}
              />
            )}
            {verticalParam && (() => {
              const lane = verticalLanes.find((l) => String(l.vertical_id) === String(verticalParam))?.lane;
              return lane ? <Chip label={`Lane: ${lane}`} size="small" variant="outlined" /> : null;
            })()}
            {contractPending ? (
              <Chip label="Contract pending" size="small" color="warning" sx={{ fontWeight: 700 }} />
            ) : contract?.state === 'parent' ? (
              <Chip label="Covered by parent agreement" size="small" color="info" variant="outlined" />
            ) : (
              <Chip label={client?.status?.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
            )}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{completedTasks}/{totalTasks} tasks</Typography>
            {estimatedLaunch && launchReason !== 'live' && (
              <Typography variant="body2" color="text.secondary">
                Est. launch: <strong style={{ color: '#ff680d' }}>{new Date(estimatedLaunch + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</strong>
              </Typography>
            )}
            {launchReason === 'live' && <Chip label="LIVE" size="small" color="success" sx={{ fontWeight: 700 }} />}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            select size="small" label="Owner" value={owner?.id || ''}
            onChange={(e) => handleSetOwner(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Unassigned</MenuItem>
            {staffOptions.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
          </TextField>
          <Button size="small" variant="contained" disableElevation startIcon={<BusinessIcon />}
            component={RouterLink} to={paths.clientHub(clientSlug)}>
            Client Hub
          </Button>
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
            href={`/onboarding/${clientSlug}`} target="_blank">
            Client View
          </Button>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" value={progress} size={56} thickness={5}
              sx={{ color: progress === 100 ? 'success.main' : 'primary.main' }} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" fontWeight={700}>{progress}%</Typography>
            </Box>
          </Box>
        </Stack>
      </Stack>

      {/* Contract gate — nothing gets built before signature */}
      {contractPending && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700}>Not signed yet — don&apos;t start building.</Typography>
          <Typography variant="body2">
            {CONTRACT_TEXT[contract.state] || 'The contract is not signed.'}
            {contract.date ? ` Last contract: ${new Date(contract.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.` : ''}
            {' '}This client stays out of lanes until it signs; onboarding starts at signature.
          </Typography>
        </Alert>
      )}

      {/* Vertical flow already live */}
      {verticalParam && verticalFirstLeadAt && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Launched</strong> — first {verticalName || 'vertical'} lead delivered{' '}
            {new Date(verticalFirstLeadAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
            {' '}This flow no longer shows on the onboarding dashboard; tick or N/A anything left for the record.
          </Typography>
        </Alert>
      )}

      {/* Whose move — land on the page and instantly know what to do next */}
      {!contractPending && (ourMoves.length > 0 || clientMoves.length > 0) && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { key: 'us', title: 'Our move', items: ourMoves, empty: 'Nothing on our side — waiting on the client.', color: 'warning.main' },
            { key: 'client', title: "Client's move", items: clientMoves, empty: 'Nothing waiting on the client.', color: 'info.main' },
          ].map((col) => (
            <Grid item xs={12} md={6} key={col.key}>
              <Card variant="outlined" sx={{ height: '100%', borderLeft: '4px solid', borderLeftColor: col.items.length ? col.color : 'success.main' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {col.title}
                  </Typography>
                  {col.items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{col.empty}</Typography>
                  ) : (
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {col.items.map((t, i) => (
                        <Stack key={t.id} direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" fontWeight={i === 0 ? 700 : 400} sx={{ fontSize: 13.5 }}>
                            {i === 0 ? '→ ' : ''}{t.template_title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t.phase}{idleDays(t) >= 3 ? ` · idle ${idleDays(t)}d` : ''}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Client Onboarding Status — client-wide journey; hidden in vertical mode */}
      {!verticalParam && (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Client Onboarding Progress</Typography>
          {verticalLanes.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: 11 }}>
              Lanes: {verticalLanes.map((l) => `${l.vertical_name} — ${l.lane}`).join(' · ')}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {completionSteps.map((step) => (
              <Chip
                key={step.key}
                label={step.label}
                size="small"
                icon={step.completed ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                color={step.completed ? 'success' : 'default'}
                variant={step.completed ? 'filled' : 'outlined'}
                sx={{ fontSize: 12 }}
              />
            ))}
          </Stack>
          {/* Client action items (pending) */}
          {clientActions.filter((a) => !a.completed).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="warning.main" fontWeight={600}>Waiting on client:</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                {clientActions.filter((a) => !a.completed).map((a) => (
                  <Chip key={a.key} label={a.label} size="small" color="warning" variant="outlined" sx={{ fontSize: 11 }} />
                ))}
              </Stack>
            </Box>
          )}
          {/* Lead Delivery Status */}
          {(() => {
            const ld = clientActions.find((a) => a.key === 'lead_delivery');
            if (!ld) return null;
            let icon, color, text;
            if (ld.completed) {
              icon = <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />;
              color = 'success.main';
              text = `Lead Delivery: Confirmed${ld.confirmed_by ? ` by ${ld.confirmed_by}` : ''}`;
            } else if (ld.tested) {
              icon = <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
              color = 'warning.main';
              text = 'Lead Delivery: Test passed — awaiting client confirmation';
            } else if (ld.has_url) {
              icon = <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
              color = 'warning.main';
              text = 'Lead Delivery: Webhook configured — awaiting test';
            } else {
              icon = <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
              color = 'text.secondary';
              text = 'Lead Delivery: Awaiting webhook setup';
            }
            return (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {icon}
                  <Typography variant="caption" fontWeight={600} sx={{ color }}>{text}</Typography>
                </Stack>
              </Box>
            );
          })()}

          {selfbookCalendar && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                <Typography variant="caption" fontWeight={600}>Self-Book Calendar:</Typography>
                <Typography variant="caption" color="primary" component="a" href={selfbookCalendar.url} target="_blank" rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline' }}>
                  {selfbookCalendar.url}
                </Typography>
              </Stack>
            </Box>
          )}
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="primary" component="a"
                href={`/projections/${clientSlug}/client-view`} target="_blank" rel="noopener noreferrer"
                sx={{ textDecoration: 'underline', fontWeight: 600 }}>
                View Projections
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Quick Actions */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" size="small" startIcon={<RocketLaunchIcon />} onClick={() => setFbDialogOpen(true)}>
          Launch Campaigns
        </Button>
        <Button variant="outlined" size="small" startIcon={<LinkIcon />} href={paths.webhookSetup(clientSlug)} target="_blank">
          Lead Delivery
        </Button>
        <Button variant="outlined" size="small" onClick={() => { setCreativeAds([]); setCreativeVerticalId(''); setCreativeUploadOpen(true); }}>
          Upload Creative
        </Button>
        <Button variant="outlined" size="small" startIcon={<LinkIcon />}
          onClick={() => { setLpUrl(''); setLpVerticalId(''); setLpName(''); setLpDialogOpen(true); }}>
          Add Landing Page
        </Button>
        <Button variant="outlined" size="small" color="secondary" onClick={openNvDialog}>
          Onboard New Vertical
        </Button>
        <Button
          variant="contained"
          size="small"
          color="success"
          startIcon={<RocketLaunchIcon />}
          onClick={async () => {
            if (!window.confirm('Send "Ready to Launch" confirmation email to the client?')) return;
            try {
              const res = await defaultClient.request({
                url: `clients/${clientSlug}/onboarding/send-ready-to-launch/`,
                method: 'POST',
              });
              toast.success(res?.data?.detail || 'Ready to launch email sent!');
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to send email');
            }
          }}
        >
          Send Ready to Launch
        </Button>
      </Stack>

      <Grid container spacing={2.5}>
        {/* Left: Creative + Landing Pages */}
        <Grid item xs={12} md={7}>
          {/* Client Inputs — Offers + Assets */}
          {onboardingData && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Client Inputs</Typography>

                {/* First-month proration choice (Confirm Budgets step) */}
                {(() => {
                  const choice = onboardingData.budgets_proration;
                  const applied = onboardingData.budgets_proration_applied;
                  const src = applied?.month ? applied : choice;
                  if (!src?.month) return null;
                  const monthName = new Date(`${src.month}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long' });
                  const usd = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`;
                  let line;
                  if (applied?.mode === 'prorate') {
                    line = `${monthName} budget: prorated to ${usd(applied.prorated_total)} (${applied.days} of ${applied.month_days} days)`;
                    if (applied.starts_on) {
                      line += `, starts ${new Date(`${applied.starts_on}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}`;
                    }
                  } else if (applied?.mode === 'full' && applied.reason === 'full') {
                    line = `Spending full ${usd(applied.full_total)} in ${monthName}`;
                  } else if (!applied && choice?.mode === 'prorate') {
                    line = `${monthName} budget: will prorate (${choice.days} of ${choice.month_days} days) when budgets are confirmed`;
                  } else if (!applied && choice?.mode === 'full') {
                    line = `Spending the full budget in ${monthName}`;
                  } else {
                    return null;
                  }
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        First Month Budget
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: 13 }}>{line}</Typography>
                    </Box>
                  );
                })()}

                {/* Assets */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Creative Assets
                  </Typography>
                  {onboardingData.use_stock_assets ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Chip label="Using Stock Media" size="small" color="info" sx={{ fontSize: 11 }} />
                      {onboardingData.assets_folder_link && (
                        <Typography variant="caption" color="primary" component="a" href={onboardingData.assets_folder_link}
                          target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline' }}>
                          Logo/reference link
                        </Typography>
                      )}
                    </Stack>
                  ) : onboardingData.assets_folder_link ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="primary" component="a" href={onboardingData.assets_folder_link}
                        target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', wordBreak: 'break-all', fontSize: 13 }}>
                        {onboardingData.assets_folder_link}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, fontSize: 13 }}>Not provided yet</Typography>
                  )}
                </Box>

                {/* Offers */}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Offers
                  </Typography>
                  {onboardingData.client_offers?.length > 0 ? (
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {(() => {
                        // Group offers by vertical
                        const byVertical = {};
                        onboardingData.client_offers.forEach((o) => {
                          const vName = o.vertical_name || 'General';
                          if (!byVertical[vName]) byVertical[vName] = [];
                          byVertical[vName].push(o);
                        });
                        return Object.entries(byVertical).map(([vName, offers]) => (
                          <Box key={vName}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                              <Chip label={vName} size="small" sx={{ bgcolor: '#111827', color: '#fff', fontWeight: 600, fontSize: 10, height: 20 }} />
                            </Stack>
                            {offers.map((o, i) => (
                              <Typography key={i} variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>
                                <strong>{o.offer_type === 'financing' ? 'Financing: ' : ''}</strong>
                                {o.display_label || o.name || o.custom_label || '—'}
                                {o.financing_details ? ` (${o.financing_details})` : ''}
                              </Typography>
                            ))}
                          </Box>
                        ));
                      })()}
                    </Stack>
                  ) : onboardingData.evergreen_offer ? (
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: 13 }}><strong>Evergreen:</strong> {onboardingData.evergreen_offer}</Typography>
                      {onboardingData.financing_offer && (
                        <Typography variant="body2" sx={{ fontSize: 13 }}><strong>Financing:</strong> {onboardingData.financing_offer}</Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, fontSize: 13 }}>Not provided yet</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Client's card for the Facebook ad account — reveal once, add, wipe */}
          <AdAccountCardStaffCard clientSlug={clientSlug} clientName={clientName} />

          {/* Launch runner: brand profile through the paused campaign build */}
          <LaunchStatusCard clientSlug={clientSlug} />

          {/* Every onboarding email sent, in one thread from clients@ */}
          <OnboardingEmailHistoryCard clientSlug={clientSlug} />

          {/* Internal Notes for Creative Designer */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Internal Notes</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Notes for the creative team (not visible to client). Include offer details, brand guidelines, tone preferences, etc.
              </Typography>
              <TextField
                multiline minRows={4} fullWidth size="small"
                placeholder="e.g., Client wants bold colors, use their tagline 'Quality You Can Trust', emphasize financing offer in ads..."
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
              />
              <Button
                size="small" variant="contained" sx={{ mt: 1.5 }}
                onClick={handleSaveInternalNotes}
                disabled={notesSaving}
              >
                {notesSaving ? 'Saving...' : 'Save Notes'}
              </Button>
            </CardContent>
          </Card>

          {/* Creative Reviews */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>Ad Creative</Typography>
                <Stack direction="row" spacing={1}>
                  {unsentAds > 0 && (
                    <Button size="small" variant="contained" disabled={sendingCreative} onClick={sendCreative}>
                      {sendingCreative ? 'Sending…' : `Send to client (${unsentAds})`}
                    </Button>
                  )}
                  <Button size="small" onClick={() => { setCreativeAds([]); setCreativeVerticalId(''); setCreativeUploadOpen(true); }}>
                    + Upload
                  </Button>
                </Stack>
              </Stack>
              {unsentAds > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {unsentAds} ad{unsentAds === 1 ? '' : 's'} not sent yet — the client sees them, in one email, when you press Send to client.
                </Typography>
              )}
              {visibleCreative.length === 0 ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">No creative uploaded yet.</Typography>
                  <Button size="small" variant="outlined" color="success" sx={{ alignSelf: 'flex-start', fontSize: 11 }}
                    onClick={async () => {
                      try {
                        await defaultClient.request({ url: 'tasks/onboarding-tasks/bulk-complete/', method: 'POST',
                          payload: { client_slug: clientSlug, template_keys: ['guru_submits_creative', 'client_approves_creative'] } });
                        toast.success('Creative step marked complete'); loadData();
                      } catch { toast.error('Failed'); }
                    }}>
                    Mark Creative Complete (handled externally)
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {visibleCreative.map((batch) => {
                    const allAssets = batch.assets || [];
                    const approved = allAssets.filter((a) => a.asset_status === 'approved');
                    return (
                      <Box key={batch.id}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {batch.vertical_name && <Chip label={batch.vertical_name} size="small" sx={{ bgcolor: '#111827', color: '#fff', fontWeight: 600, fontSize: 11 }} />}
                            <Chip label={batch.status_display} size="small" variant={batch.status === 'draft' ? 'outlined' : 'filled'}
                              color={batch.status.includes('approved') ? 'success' : batch.status === 'changes_requested' ? 'warning' : batch.status === 'draft' ? 'default' : 'info'} />
                            <Typography variant="caption" color="text.secondary">
                              {approved.length}/{allAssets.length} approved
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack spacing={0.75}>
                          {allAssets.map((asset) => (
                            <Stack key={asset.id} direction="row" spacing={1.5} alignItems="center"
                              sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: asset.asset_status === 'approved' ? '#f0fdf4' : asset.asset_status === 'changes_requested' ? '#fff8f0' : '#fff' }}>
                              <Box sx={{ width: 48, height: 48, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: '#f5f5f5' }}>
                                {asset.asset_type === 'video' ? (
                                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#222' }}>
                                    <Typography sx={{ color: '#fff', fontSize: 14 }}>&#9654;</Typography>
                                  </Box>
                                ) : (
                                  <img src={asset.file_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{asset.label || 'Ad'}</Typography>
                                  <Chip label={asset.asset_status} size="small"
                                    color={asset.asset_status === 'approved' ? 'success' : asset.asset_status === 'changes_requested' ? 'warning' : 'default'}
                                    sx={{ fontSize: 10, height: 18 }} />
                                  {asset.awaiting_send && (
                                    <Chip label="Not sent" size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                                  )}
                                </Stack>
                                {(asset.comments || []).length > 0 && (
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontStyle: 'italic', fontSize: 11 }}>
                                    {(() => { const last = asset.comments[asset.comments.length - 1]; return `${last.author}: ${last.text}`; })()}
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                {(asset.comments || []).some((c) => c.role === 'client') && (
                                  <Button size="small" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={() => setFeedbackAsset(asset)}>
                                    Feedback{(asset.comments || []).some((c) => c.pin) ? ' 📍' : ''}
                                  </Button>
                                )}
                                {asset.asset_status !== 'approved' && (
                                  <Button size="small" variant={asset.asset_status === 'changes_requested' ? 'contained' : 'text'} color="warning"
                                    sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={() => setReplaceAsset(asset)}>
                                    Replace
                                  </Button>
                                )}
                                {asset.asset_status !== 'approved' && (
                                  <Button size="small" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async (e) => {
                                    e.stopPropagation();
                                    try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/asset/${asset.id}/action/`, method: 'POST', payload: { action: 'approve' } }); toast.success('Approved'); refreshCreative(); } catch { toast.error('Failed'); }
                                  }}>Approve</Button>
                                )}
                                {asset.asset_status !== 'pending' && (
                                  <Button size="small" color="warning" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async (e) => {
                                    e.stopPropagation();
                                    try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/asset/${asset.id}/action/`, method: 'POST', payload: { action: 'reset' } }); toast.success('Reset to pending'); refreshCreative(); } catch { toast.error('Failed'); }
                                  }}>Reset</Button>
                                )}
                                <Button size="small" color="error" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Remove "${asset.label || 'this ad'}"? This cannot be undone.`)) return;
                                  try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/asset/${asset.id}/`, method: 'DELETE' }); toast.success('Removed'); refreshCreative(); } catch { toast.error('Failed'); }
                                }}>Remove</Button>
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Landing Pages */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>Landing Pages</Typography>
                <Button size="small" onClick={() => { setLpUrl(''); setLpVerticalId(''); setLpName(''); setLpDialogOpen(true); }}>
                  + Add
                </Button>
              </Stack>
              {visibleLps.length === 0 ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">No landing pages added yet.</Typography>
                  <Button size="small" variant="outlined" color="success" sx={{ alignSelf: 'flex-start', fontSize: 11 }}
                    onClick={async () => {
                      try {
                        await defaultClient.request({ url: 'tasks/onboarding-tasks/bulk-complete/', method: 'POST',
                          payload: { client_slug: clientSlug, template_keys: ['landing_pages_built', 'landing_pages_tested'] } });
                        toast.success('LP step marked complete'); loadData();
                      } catch { toast.error('Failed'); }
                    }}>
                    Mark LP Complete (handled externally)
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {visibleLps.map((lp) => {
                    const comments = lp.comments || [];
                    const lastComment = comments.length ? comments[comments.length - 1] : null;
                    return (
                      <Stack key={lp.id} direction="row" spacing={1.5} alignItems="center"
                        sx={{
                          p: 1.5, border: '1px solid', borderRadius: 1,
                          borderColor: lp.approved ? 'success.main' : lp.status === 'changes_requested' ? '#f59e0b' : '#e0e0e0',
                          bgcolor: lp.approved ? '#f0fdf4' : lp.status === 'changes_requested' ? '#fffbeb' : '#fff',
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip label={lp.vertical_name || 'General'} size="small" sx={{ bgcolor: '#111827', color: '#fff', fontWeight: 600, fontSize: 11 }} />
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{lp.name}</Typography>
                            <Chip label={lp.approved ? 'Approved' : lp.status === 'changes_requested' ? 'Changes' : 'Pending'} size="small"
                              color={lp.approved ? 'success' : lp.status === 'changes_requested' ? 'warning' : 'default'} sx={{ fontSize: 10, height: 18 }} />
                          </Stack>
                          <Typography variant="caption" color="primary" component="a" href={lp.url} target="_blank" rel="noopener noreferrer"
                            sx={{ textDecoration: 'underline', wordBreak: 'break-all', fontSize: 11 }}>{lp.url}</Typography>
                          {lastComment && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontStyle: 'italic', fontSize: 11 }}>
                              {`${lastComment.author}: ${lastComment.text}`}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                          {!lp.approved && (
                            <Button size="small" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async () => {
                              try { await defaultClient.request({ url: `clients/${clientSlug}/onboarding/landing-page/${lp.id}/approve/`, method: 'POST', payload: { action: 'approve' } }); toast.success('Approved'); refreshLandingPages(); loadData(); } catch { toast.error('Failed'); }
                            }}>Approve</Button>
                          )}
                          {lp.approved && (
                            <Button size="small" color="warning" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async () => {
                              try { await defaultClient.request({ url: `clients/${clientSlug}/onboarding/landing-page/${lp.id}/approve/`, method: 'POST', payload: { action: 'reset' } }); toast.success('Reset'); refreshLandingPages(); loadData(); } catch { toast.error('Failed'); }
                            }}>Reset</Button>
                          )}
                          <Button size="small" color="error" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async () => {
                            try { await defaultClient.request({ url: `app1/landing-pages/${clientSlug}/${lp.id}/`, method: 'DELETE' }); toast.success('Removed'); refreshLandingPages(); } catch { toast.error('Failed'); }
                          }}>Remove</Button>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>

        </Grid>

        {/* Right: Task Checklist */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ position: 'sticky', top: 80 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Task Checklist</Typography>
              <Stack spacing={1.5}>
                {Object.entries(phases).map(([phaseName, phaseTasks]) => {
                  const phaseCounted = phaseTasks.filter((t) => t.status !== 'not_applicable');
                  const phaseComplete = phaseCounted.filter((t) => t.status === 'complete').length;
                  const phaseTotal = phaseCounted.length;
                  const phaseProgress = phaseTotal ? Math.round((phaseComplete / phaseTotal) * 100) : 100;

                  return (
                    <Accordion key={phaseName} defaultExpanded={phaseProgress < 100} disableGutters
                      sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px !important', '&:before': { display: 'none' }, overflow: 'hidden' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontSize: 13 }}>{phaseName}</Typography>
                          <Typography variant="caption" color={phaseProgress === 100 ? 'success.main' : 'text.secondary'} fontWeight={600}>
                            {phaseComplete}/{phaseTotal}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, px: 1 }}>
                        <Stack spacing={0}>
                          {phaseTasks.sort((a, b) => (a.template_sort_order || 0) - (b.template_sort_order || 0)).map((task) => {
                            const isAutoComplete = task.status === 'complete' && task.evidence?.auto_completed;
                            const isNA = task.status === 'not_applicable';
                            const autoReady = !isNA && task.status !== 'complete' && task.evidence?.would_auto_complete;
                            return (
                              <Stack key={task.id} direction="row" alignItems="center" spacing={0.5}
                                sx={{ py: 0.5, px: 0.5, borderRadius: 0.5, '&:hover': { bgcolor: 'grey.50' } }}>
                                <Checkbox
                                  checked={task.status === 'complete'}
                                  onChange={() => handleToggleComplete(task)}
                                  disabled={isAutoComplete || isNA || pendingTaskIds.has(task.id)}
                                  size="small" sx={{ p: 0.25 }}
                                />
                                <Typography variant="body2" sx={{
                                  flex: 1, fontSize: 12,
                                  fontStyle: isNA ? 'italic' : 'normal',
                                  textDecoration: task.status === 'complete' ? 'line-through' : 'none',
                                  color: task.status === 'complete' || isNA ? 'text.disabled' : 'text.primary',
                                }}>
                                  {task.template_title}
                                </Typography>
                                {isAutoComplete && (
                                  <LockIcon
                                    sx={{ fontSize: 12, color: 'text.disabled' }}
                                    titleAccess={`Auto-verified${task.evidence?.auto_completed_at ? ` ${shortDate(task.evidence.auto_completed_at)}` : ''}`}
                                  />
                                )}
                                {autoReady && (
                                  <Chip
                                    label={`auto ✓${task.evidence?.would_auto_complete_at ? ` ${shortDate(task.evidence.would_auto_complete_at)}` : ''}`}
                                    size="small" color="info" variant="outlined"
                                    title="Verified by an auto-check rule (shadow mode) — will self-complete once the rule goes live"
                                    sx={{ fontSize: 10, height: 16 }}
                                  />
                                )}
                                {(isAutoComplete || autoReady) && EVIDENCE_LINKS[task.template_key] && (
                                  <Typography
                                    variant="caption" color="primary" component="a"
                                    href={EVIDENCE_LINKS[task.template_key]} target="_blank" rel="noopener noreferrer"
                                    sx={{ fontSize: 10, textDecoration: 'underline', flexShrink: 0 }}
                                  >
                                    view
                                  </Typography>
                                )}
                                {isNA && <Chip label="N/A" size="small" sx={{ fontSize: 9, height: 16 }} />}
                                <Typography
                                  variant="caption" color="text.secondary" sx={{ fontSize: 10, cursor: 'pointer', flexShrink: 0 }}
                                  onClick={() => handleSetStatus(task, isNA ? 'not_started' : 'not_applicable')}
                                >
                                  {isNA ? 'Restore' : 'N/A'}
                                </Typography>
                                <Typography
                                  variant="caption" color="primary" sx={{ fontSize: 10, cursor: 'pointer', flexShrink: 0 }}
                                  onClick={() => { setNotesDialog(task.id); setNotesValue(task.notes || ''); }}
                                >
                                  {task.notes ? 'Edit' : 'Note'}
                                </Typography>
                              </Stack>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                {!verticalParam && (
                  <Accordion disableGutters defaultExpanded={false}
                    onChange={(e, expanded) => { if (expanded) loadArchived(); }}
                    sx={{ boxShadow: 'none', border: '1px dashed #d0d0d0', borderRadius: '8px !important', '&:before': { display: 'none' }, overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                        Archived items (pre-v2 checklist)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 1 }}>
                      {archivedLoading ? (
                        <CircularProgress size={18} />
                      ) : (archivedTasks || []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No archived history for this client.
                        </Typography>
                      ) : (
                        <Stack spacing={0}>
                          {(archivedTasks || []).map((task) => (
                            <Stack key={task.id} direction="row" alignItems="flex-start" spacing={0.75} sx={{ py: 0.5, px: 0.5 }}>
                              {task.status === 'complete'
                                ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', mt: '2px' }} />
                                : <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '2px' }} />}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{
                                  fontSize: 12, color: 'text.disabled',
                                  textDecoration: task.status === 'complete' ? 'line-through' : 'none',
                                }}>
                                  {task.template_title}
                                </Typography>
                                {task.notes && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, fontStyle: 'italic', display: 'block' }}>
                                    {task.notes}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== DIALOGS ===== */}

      {/* Start new-vertical flow dialog */}
      <Dialog open={nvDialogOpen} onClose={() => setNvDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Onboard New Vertical</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Starts a per-vertical checklist for {clientName}: offer, markets, posting,
            creative, landing page, campaigns, and budget for the new vertical.
          </Typography>
          <TextField select label="Vertical" value={nvVerticalId} fullWidth size="small"
            onChange={(e) => setNvVerticalId(e.target.value)}>
            <MenuItem value="">Select vertical...</MenuItem>
            {nvVerticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNvDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!nvVerticalId || nvStarting} onClick={async () => {
            setNvStarting(true);
            const start = (force) => defaultClient.request({
              url: 'tasks/onboarding-tasks/start-vertical/',
              method: 'POST',
              payload: { client_slug: clientSlug, vertical_id: nvVerticalId, force },
            });
            try {
              try {
                await start(false);
              } catch (err) {
                // Already live in this vertical: only a deliberate relaunch
                // should open a flow.
                if (!err?.response?.data?.already_live) throw err;
                if (!window.confirm(`${err.response.data.detail}\n\nStart a new-vertical flow anyway (e.g. a relaunch)?`)) return;
                await start(true);
              }
              toast.success('Vertical onboarding started');
              setNvDialogOpen(false);
              setSearchParams({ vertical: String(nvVerticalId) });
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to start vertical onboarding');
            } finally {
              setNvStarting(false);
            }
          }}>
            {nvStarting ? 'Starting...' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes dialog */}
      <Dialog open={!!notesDialog} onClose={() => setNotesDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Task Notes</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} value={notesValue} onChange={(e) => setNotesValue(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialog(null)}>Cancel</Button>
          <Button onClick={handleSaveNotes} variant="contained" disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Landing Page Dialog */}
      <Dialog open={lpDialogOpen} onClose={() => setLpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Landing Page</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Vertical *" value={lpVerticalId} fullWidth onChange={(e) => setLpVerticalId(e.target.value)}>
              <MenuItem value="">Select vertical...</MenuItem>
              {lpVerticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
            <TextField label="Landing Page URL *" placeholder="https://client-lp.leadgurus.com" value={lpUrl} onChange={(e) => setLpUrl(e.target.value)} fullWidth />
            <TextField label="Display Name (optional)" value={lpName} onChange={(e) => setLpName(e.target.value)} fullWidth size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLpDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!lpUrl || !lpVerticalId || lpSaving} onClick={async () => {
            setLpSaving(true);
            try { await defaultClient.request({ url: `app1/landing-pages/${clientSlug}/`, method: 'POST', payload: { url: lpUrl, vertical_id: lpVerticalId, name: lpName } }); toast.success('Added!'); setLpDialogOpen(false); refreshLandingPages(); loadData(); }
            catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
            finally { setLpSaving(false); }
          }}>{lpSaving ? 'Saving...' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Creative Upload Dialog */}
      <Dialog open={creativeUploadOpen} onClose={() => setCreativeUploadOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">Upload Ad Creative — Batch Upload</Typography>
            {creativeAds.length > 0 && (
              <Chip label={`${creativeAds.length} ad${creativeAds.length !== 1 ? 's' : ''} queued`} size="small" color="primary" />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Select <strong>one or more</strong> images/videos at once — they upload as a single batch and the client reviews them together. You can add more files at any time before submitting.
            </Alert>
            <TextField select label="Vertical *" value={creativeVerticalId} fullWidth size="small" onChange={(e) => setCreativeVerticalId(e.target.value)} helperText="Which vertical are these ads for?">
              <MenuItem value="">Select vertical...</MenuItem>
              {lpVerticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>

            {/* Primary file picker — prominent at top so multi-select is obvious */}
            <Button
              variant={creativeAds.length === 0 ? 'contained' : 'outlined'}
              component="label"
              size="large"
              sx={{ textTransform: 'none', py: creativeAds.length === 0 ? 2 : 1, borderStyle: creativeAds.length === 0 ? 'dashed' : 'solid' }}
              fullWidth
            >
              {creativeAds.length === 0
                ? '+ Select Ad Files (you can pick multiple at once)'
                : `+ Add More Files to This Batch`}
              <input type="file" hidden multiple accept="image/*,video/*" onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setCreativeAds((prev) => [...prev, ...files.map((f) => ({ file: f, headline: '', primary_text: '', description: '', cta_type: 'LEARN_MORE', audience_target: 'both' }))]);
                e.target.value = '';
              }} />
            </Button>

            {creativeAds.length === 0 && (
              <Typography variant="caption" color="text.secondary" align="center">
                Hold ⌘ (Mac) or Ctrl (Windows) in the file picker to select multiple files. No files queued yet.
              </Typography>
            )}

            {creativeAds.map((ad, idx) => (
              <Card key={idx} variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Ad {idx + 1}: {ad.file?.name || 'No file'}</Typography>
                    <Button size="small" color="error" onClick={() => setCreativeAds((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button>
                  </Stack>
                  <Stack spacing={1.5}>
                    <TextField label="Headline" size="small" fullWidth value={ad.headline} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, headline: e.target.value } : a))} />
                    <TextField label="Primary Text" size="small" fullWidth multiline minRows={2} value={ad.primary_text} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, primary_text: e.target.value } : a))} />
                    <Stack direction="row" spacing={1.5}>
                      <TextField label="Description" size="small" fullWidth value={ad.description} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, description: e.target.value } : a))} />
                      <TextField select label="CTA" size="small" value={ad.cta_type} sx={{ minWidth: 140 }} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, cta_type: e.target.value } : a))}>
                        {[['LEARN_MORE','Learn More'],['SIGN_UP','Sign Up'],['GET_QUOTE','Get Quote'],['CONTACT_US','Contact Us'],['BOOK_NOW','Book Now'],['CALL_NOW','Call Now'],['GET_OFFER','Get Offer']].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                      </TextField>
                    </Stack>
                    <TextField select label="Audience target" size="small" fullWidth value={ad.audience_target || 'both'} helperText="Which ad set should this creative run in" onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, audience_target: e.target.value } : a))}>
                      <MenuItem value="both">Both ad sets</MenuItem>
                      <MenuItem value="advantage_plus">Advantage+ Broad only</MenuItem>
                      <MenuItem value="broad_55_plus">55+ Broad only</MenuItem>
                    </TextField>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreativeUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={creativeAds.length === 0 || creativeUploading} onClick={async () => {
            setCreativeUploading(true);
            try {
              const formData = new FormData();
              if (creativeVerticalId) formData.append('vertical_id', creativeVerticalId);
              creativeAds.forEach((ad) => { formData.append('files', ad.file); formData.append('labels', ad.file.name); formData.append('headlines', ad.headline); formData.append('primary_texts', ad.primary_text); formData.append('descriptions', ad.description); formData.append('cta_types', ad.cta_type); formData.append('audience_targets', ad.audience_target || 'both'); });
              const uploadRes = await defaultClient.request({ url: `app1/creative-review/${clientSlug}/upload/`, method: 'POST', payload: formData, options: { headers: { 'Content-Type': 'multipart/form-data' } } });
              setCreativeUploadOpen(false);
              if (uploadRes?.data?.id) toast.success('Saved as a draft — press "Send to client" when everything is ready.');
              refreshCreative(); loadData();
            } catch { toast.error('Upload failed'); } finally { setCreativeUploading(false); }
          }}>
            {creativeUploading ? 'Uploading...' : `Upload ${creativeAds.length} Ad${creativeAds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      <CreativeReplaceDialog asset={replaceAsset} clientSlug={clientSlug}
        onClose={() => setReplaceAsset(null)} onSaved={() => { setReplaceAsset(null); refreshCreative(); }} />
      <CreativeFeedbackDialog asset={feedbackAsset} onClose={() => setFeedbackAsset(null)} />

      {/* Launch panel: readiness → background build → Go live */}
      <CampaignLaunchDialog
        open={fbDialogOpen}
        onClose={() => setFbDialogOpen(false)}
        clientSlug={clientSlug}
        clientName={clientName}
        onBuilt={loadData}
      />
    </Box>
  );
}
